import React, {FC, RefCallback} from 'react';
import styled from 'styled-components';

import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {isEmptyNonEmailDraft} from '../../../../../store/data/messages/draftMessagesActions';
import {MessageDraftsProps, renderMessageDrafts} from './messageDrafts';

/*
 * Types.
 */

interface BottomMessageDraftsProps extends MessageDraftsProps {
  timelineVariation: TimelineVariationsEnum;
  bottomComposerRef: RefCallback<HTMLDivElement>;
  bottomComposerHeight: number;
}

interface StyledMessageDraftsBoxPlaceholderDivProps {
  $height: number;
}

/*
 * Constants.
 */

const StyledComposerDiv = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding-left: 5px;
  padding-right: 15px;
`;

const StyledMessageDraftsBoxPlaceholderDiv = styled.div<StyledMessageDraftsBoxPlaceholderDivProps>`
  height: ${(p) => computePlaceholderHeight(p.$height)}px;
  width: 100%;
`;

/*
 * Component.
 */

/**
 * Wrapper around MessageDrafts which handles rendering drafts stuck to the bottom of the timeline, and handles
 * the spacing logic to still have items in the real timeline scroll correctly underneath the sticky composer.
 */
export const BottomMessageDrafts: FC<BottomMessageDraftsProps> = (props) => {
  const {userId, timelineVariation, bottomComposerRef, bottomComposerHeight} = props;

  // Fill the bottom of the timeline with empty space so that content scrolls correctly underneath.
  const placeholder = props.composeDraftMessages.length > 0 && (
    <StyledMessageDraftsBoxPlaceholderDiv $height={bottomComposerHeight} />
  );

  // In a synchronous context (where we show the draft on the bottom), it is only possible for two+ self-authored
  // drafts to appear while a message is going through the send process. This check is used to prevent messages
  // from momentarily blinking back into the draft state.
  const hasSendingDraft =
    props.composeDraftMessages.length > 1 &&
    props.composeDraftMessages.some(
      (draftMessage) => draftMessage.authorId === userId && !isEmptyNonEmailDraft(draftMessage),
    ) &&
    props.composeDraftMessages.some(
      (draftMessage) => draftMessage.authorId === userId && isEmptyNonEmailDraft(draftMessage),
    );

  // 1. No self-authored drafts are being sent, render all drafts.
  // 2. If any drafts are being sent, only render:
  //   - Empty drafts.
  //   - Drafts authored by other teammates.
  const unsentComposeDraftMessages = props.composeDraftMessages.filter(
    (draftMessage) =>
      !hasSendingDraft || isEmptyNonEmailDraft(draftMessage) || draftMessage.authorId !== userId,
  );

  const bottomDraftsProps: MessageDraftsProps = {
    ...props,
    timelineVariation,
    isInComposerOnBottom: true,
    composeDraftMessages: unsentComposeDraftMessages,
    dontFocusComposer: timelineVariation === TimelineVariationsEnum.SYNCHRONOUS,
  };

  return (
    <>
      {placeholder}
      <StyledComposerDiv ref={bottomComposerRef}>{renderMessageDrafts(bottomDraftsProps)}</StyledComposerDiv>
    </>
  );
};

/*
 * Helpers.
 */

function computePlaceholderHeight(composerWrapperHeight: number) {
  // Subtract 8px to have a total gap of 8px between the composer and timeline.
  return Math.max(composerWrapperHeight - 8, 0);
}
